import { keepPreviousData, useQuery } from '@tanstack/react-query';

import type {
  AuditLog,
  AuditLogFilters,
  AuditLogStats,
  OffsetPaginationInfo,
} from '@mai/types';

import { type QueryOptions, apiClient } from './axios';

// Paginated response type
export type PaginatedAuditLogs = {
  items: AuditLog[];
  pagination: OffsetPaginationInfo;
};

// Fetch audit logs with filtering
export const fetchAuditLogs = async (
  filters?: AuditLogFilters,
): Promise<PaginatedAuditLogs> => {
  const response = await apiClient.get<PaginatedAuditLogs>('/audit-logs', {
    params: filters,
  });
  return response.data;
};

// Fetch audit log statistics
export const fetchAuditLogStats = async (
  teamId: string,
  filters?: Omit<AuditLogFilters, 'teamId'>,
): Promise<AuditLogStats> => {
  const response = await apiClient.get<AuditLogStats>('/audit-logs/stats', {
    params: { teamId, ...filters },
  });
  return response.data;
};

// Fetch single audit log by ID
export const fetchAuditLogById = async (
  auditLogId: string,
): Promise<AuditLog> => {
  const response = await apiClient.get<AuditLog>(`/audit-logs/${auditLogId}`);
  return response.data;
};

// React Query hooks

export const useAuditLogs = (
  teamId: string | null,
  filters?: Omit<AuditLogFilters, 'teamId'>,
  options?: QueryOptions,
) => {
  return useQuery({
    queryKey: ['audit-logs', teamId, filters],
    queryFn: () =>
      fetchAuditLogs({
        teamId: teamId!,
        ...filters,
      }),
    enabled: !!teamId && options?.enabled !== false,
    placeholderData: keepPreviousData,
    ...options,
  });
};

export const useAuditLogStats = (
  teamId: string | null,
  filters?: Omit<AuditLogFilters, 'teamId'>,
  options?: QueryOptions,
) => {
  return useQuery({
    queryKey: ['audit-log-stats', teamId, filters],
    queryFn: () => fetchAuditLogStats(teamId!, filters),
    enabled: !!teamId && options?.enabled !== false,
    ...options,
  });
};

export const useAuditLog = (
  auditLogId: string | null,
  options?: QueryOptions,
) => {
  return useQuery({
    queryKey: ['audit-log', auditLogId],
    queryFn: () => fetchAuditLogById(auditLogId!),
    enabled: !!auditLogId && options?.enabled !== false,
    ...options,
  });
};
